import React from 'react'
import { Link as LinkTo } from 'react-router-dom'
import { makeStyles } from '@material-ui/styles'
import Box from '@material-ui/core/Box'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import Badge from '@material-ui/core/Badge'
import Tooltip from '@material-ui/core/Tooltip'
import IconButton from '@material-ui/core/IconButton'
import RecordBookingPaymentDialog from 'components/record-booking-payment-dialog'
import CancelEventRegistrationDialog from 'components/cancel-event-registration-dialog'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import PaymentIcon from 'components/material-symbol/icons/Payment'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import { formatPhoneNumber } from 'utils/phoneNumber'
import { toFullDateAndTime } from 'utils/dateUtils'
import { getInitials } from 'lib/text-display'
import CollectPaymentDialog from './collect-payment-dialog'
import { PaymentDetails } from './payment-details'
import {
  PaymentStatusChip,
  usePaymentStatusStyles,
} from './payment-status-chip'
import { getRsvpStatus } from './utils'

const useStyles = makeStyles(theme => ({
  tableCell: {
    height: 'auto !important',
    minHeight: '72px',
    verticalAlign: 'top',
  },
  nameTableCell: {
    overflow: 'visible',
    minWidth: 0,
  },
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  statusInfo: {
    minWidth: 0,
    whiteSpace: 'normal',
    overflow: 'visible',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  avatarBadge: {
    flexShrink: 0,
    overflow: 'visible',
  },
  attendeeAvatar: {
    height: 36,
    width: 36,
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '13px',
    fontWeight: 500,
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  actionButtons: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  collectPayment: {
    width: '100%',
    marginTop: theme.spacing(0.5),
  },
  borderedActionButton: {
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'color'],
      { duration: theme.transitions.duration.short },
    ),
    '&:hover': {
      backgroundColor: '#EBEBEB',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
}))

const HopeLinkWrapper = ({ condition, to, children }) => {
  if (!condition) return children

  return (
    <LinkTo to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {children}
    </LinkTo>
  )
}

const getEnrollmentTransactions = (enrollment, event) => {
  const studentId = enrollment.student.id
  const sessionEnrollments = event.sessions?.[0]?.enrollments

  if (sessionEnrollments) {
    return sessionEnrollments.find(item => item.student.id === studentId)
      ?.transactions
  }

  return event.enrollments.find(item => item.student.id === studentId)
    ?.transactions
}

const AttendeeListItem = ({
  enrollment,
  event,
  isHopeEvent = false,
  studentProfileBasePath,
  isUserPrimaryCoach = false,
}) => {
  const classes = useStyles()
  const statusClasses = usePaymentStatusStyles()
  const { student, createdAt, isCanceled } = enrollment
  const studentName = `${student.firstName} ${student.lastName}`
  const studentInitials = getInitials(studentName)
  const studentProfilePath = `${studentProfileBasePath}/${student.id}`
  const rsvpStatusIcon = getRsvpStatus(enrollment, event)

  const transactions = getEnrollmentTransactions(enrollment, event)
  const isPaid = transactions?.some(item => !item.refund)
  const paymentDate = isPaid && transactions?.[0]?.createdAt
  const registrationPaymentId =
    isPaid && transactions?.[0]?.registrationPayment?.id

  const contactInformation = [
    ...new Set(
      student.representatives
        .flatMap(representative => [
          representative.contact.email,
          formatPhoneNumber(representative.contact.phone),
        ])
        .filter(value => value != null && value.trim().length > 0),
    ),
  ].join(' | ')

  const { title, startDateTime, priceInCents, acceptsOnlinePayments } = event

  return (
    <TableRow data-testid={`attendee-list-row-${enrollment.id}`}>
      <TableCell className={`${classes.tableCell} ${classes.info}`}>
        <Box className={classes.nameCell}>
          <HopeLinkWrapper condition={isHopeEvent} to={studentProfilePath}>
            <Badge
              invisible={!rsvpStatusIcon}
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              badgeContent={rsvpStatusIcon}
              className={classes.avatarBadge}
            >
              <Avatar className={classes.attendeeAvatar}>
                {studentInitials}
              </Avatar>
            </Badge>
          </HopeLinkWrapper>
          <Box className={classes.info}>
            <HopeLinkWrapper condition={isHopeEvent} to={studentProfilePath}>
              <Typography className={classes.text} noWrap>
                {studentName}
              </Typography>
            </HopeLinkWrapper>
            {contactInformation ? (
              <Typography className={classes.textSecondary} noWrap>
                {contactInformation}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </TableCell>
      <TableCell className={`${classes.tableCell} ${classes.statusInfo}`}>
        <Box className={statusClasses.statusCell}>
          <PaymentStatusChip isPaid={isPaid} />
          {isPaid ? (
            <PaymentDetails
              paymentDate={paymentDate}
              registrationPaymentId={registrationPaymentId}
              showLink={isUserPrimaryCoach}
            />
          ) : null}
          <Typography className={classes.textSecondary}>
            Registered {toFullDateAndTime(createdAt)}
          </Typography>
          {!isPaid && !isCanceled ? (
            <Box className={classes.collectPayment}>
              <SensitiveInfoWrapper>
                <CollectPaymentDialog event={event} enrollment={enrollment} />
              </SensitiveInfoWrapper>
            </Box>
          ) : null}
        </Box>
      </TableCell>
      <TableCell className={`${classes.tableCell} ${classes.actions}`}>
        <Box className={classes.actionButtons}>
          {!isPaid && !isCanceled ? (
            <SensitiveInfoWrapper>
              <RecordBookingPaymentDialog
                trigger={
                  <Tooltip title="Mark as Paid">
                    <IconButton
                      aria-label="Mark as Paid"
                      className={classes.borderedActionButton}
                    >
                      <PaymentIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
                enrollment={enrollment}
                eventTitle={title}
                eventDate={startDateTime}
                participantName={studentName}
                defaultAmountInCents={priceInCents}
                acceptOnlinePayments={acceptsOnlinePayments}
              />
            </SensitiveInfoWrapper>
          ) : null}
          {!isCanceled ? (
            <CancelEventRegistrationDialog
              trigger={
                <Tooltip title="Cancel Event Registration">
                  <IconButton
                    aria-label="Cancel Event Registration"
                    className={classes.borderedActionButton}
                  >
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              studentId={student.id}
              eventId={event.id}
              acceptsOnlinePayments={acceptsOnlinePayments}
            />
          ) : null}
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default AttendeeListItem
